import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, XCircle, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { createBooking, findOrCreateClient } from "@/utils/bookingDatabase";

const SESSION_KEY = "figgy_pending_booking";
const MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes

interface PendingBooking {
  businessId: string;
  returnUrl: string;
  serviceId: string;
  serviceDuration: number;
  bookingDate: string; // ISO string
  bookingTime: string;
  customerFirstName: string;
  customerLastName: string;
  customerEmail: string;
  customerPhone: string;
  savedAt: number;
}

type PageState =
  | { status: "loading" }
  | { status: "success"; confirmationNumber: string; returnUrl: string }
  | { status: "already_created"; returnUrl: string }
  | { status: "payment_failed"; returnUrl: string }
  | { status: "payment_canceled"; returnUrl: string }
  | { status: "stale_session" }
  | { status: "booking_error"; message: string; returnUrl: string };

function readPendingReturnUrl(): string {
  const raw = sessionStorage.getItem(SESSION_KEY);
  if (!raw) return "/";
  try {
    const parsed = JSON.parse(raw) as Partial<PendingBooking>;
    return typeof parsed?.returnUrl === "string" && parsed.returnUrl ? parsed.returnUrl : "/";
  } catch {
    return "/";
  }
}

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const [pageState, setPageState] = useState<PageState>({ status: "loading" });

  useEffect(() => {
    const run = async () => {
      const redirectStatus = searchParams.get("redirect_status");
      const paymentIntentId = searchParams.get("payment_intent");

      // Handle non-success redirect statuses immediately
      if (redirectStatus === "failed") {
        setPageState({ status: "payment_failed", returnUrl: readPendingReturnUrl() });
        return;
      }
      if (redirectStatus === "canceled") {
        setPageState({ status: "payment_canceled", returnUrl: readPendingReturnUrl() });
        return;
      }

      // Expect succeeded
      if (redirectStatus !== "succeeded" || !paymentIntentId) {
        setPageState({ status: "stale_session" });
        return;
      }

      // Read pending booking from sessionStorage
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (!raw) {
        setPageState({ status: "stale_session" });
        return;
      }

      let pending: PendingBooking;
      try {
        pending = JSON.parse(raw);
      } catch {
        setPageState({ status: "stale_session" });
        return;
      }

      if (Date.now() - pending.savedAt > MAX_AGE_MS) {
        sessionStorage.removeItem(SESSION_KEY);
        setPageState({ status: "stale_session" });
        return;
      }

      // Create the booking
      try {
        const clientResult = await findOrCreateClient({
          firstName: pending.customerFirstName,
          lastName: pending.customerLastName,
          email: pending.customerEmail,
          phone: pending.customerPhone,
          businessId: pending.businessId,
        });

        const result = await createBooking({
          businessId: pending.businessId,
          serviceId: pending.serviceId,
          bookingDate: new Date(pending.bookingDate),
          bookingTime: pending.bookingTime,
          duration: pending.serviceDuration,
          customerName: `${pending.customerFirstName} ${pending.customerLastName}`,
          customerEmail: pending.customerEmail,
          customerPhone: pending.customerPhone,
          paymentIntentId,
          clientId: clientResult.success ? clientResult.clientId : undefined,
        });

        if (!result.success) {
          console.error("[PaymentSuccessPage] Booking creation failed after 3DS:", result.error);
          sessionStorage.removeItem(SESSION_KEY);
          setPageState({
            status: "booking_error",
            message:
              "Your payment was received but we couldn't save your booking. Please contact support with payment reference: " +
              paymentIntentId.slice(-8).toUpperCase(),
            returnUrl: pending.returnUrl,
          });
          return;
        }

        sessionStorage.removeItem(SESSION_KEY);
        setPageState({
          status: "success",
          confirmationNumber: result.confirmationNumber || "",
          returnUrl: pending.returnUrl,
        });
      } catch (err: any) {
        console.error("[PaymentSuccessPage] Unexpected error:", err);
        sessionStorage.removeItem(SESSION_KEY);
        setPageState({
          status: "booking_error",
          message:
            "Your payment was received but we encountered an error. Please contact support with payment reference: " +
            paymentIntentId.slice(-8).toUpperCase(),
          returnUrl: pending?.returnUrl || "/",
        });
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-8 pb-8 flex flex-col items-center text-center gap-4">
          {pageState.status === "loading" && (
            <>
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <p className="text-muted-foreground">Confirming your booking…</p>
            </>
          )}

          {pageState.status === "success" && (
            <>
              <CheckCircle className="h-14 w-14 text-green-500" />
              <h1 className="text-2xl font-bold">Booking Confirmed</h1>
              {pageState.confirmationNumber && (
                <p className="text-sm text-muted-foreground">
                  Confirmation #{pageState.confirmationNumber}
                </p>
              )}
              <p className="text-sm text-muted-foreground">
                A confirmation email is on its way to you.
              </p>
              <Button asChild className="mt-2 w-full">
                <Link to={pageState.returnUrl}>Back to booking page</Link>
              </Button>
            </>
          )}

          {pageState.status === "already_created" && (
            <>
              <CheckCircle className="h-14 w-14 text-green-500" />
              <h1 className="text-2xl font-bold">Booking Confirmed</h1>
              <Button asChild className="mt-2 w-full">
                <Link to={pageState.returnUrl}>Back to booking page</Link>
              </Button>
            </>
          )}

          {pageState.status === "payment_failed" && (
            <>
              <XCircle className="h-14 w-14 text-destructive" />
              <h1 className="text-2xl font-bold">Payment Failed</h1>
              <p className="text-sm text-muted-foreground">
                Your card was not charged. Please try again.
              </p>
              <Button asChild variant="outline" className="mt-2 w-full">
                <Link to={pageState.returnUrl}>Try again</Link>
              </Button>
            </>
          )}

          {pageState.status === "payment_canceled" && (
            <>
              <XCircle className="h-14 w-14 text-muted-foreground" />
              <h1 className="text-2xl font-bold">Payment Canceled</h1>
              <p className="text-sm text-muted-foreground">No charge was made.</p>
              <Button asChild variant="outline" className="mt-2 w-full">
                <Link to={pageState.returnUrl}>Go back</Link>
              </Button>
            </>
          )}

          {pageState.status === "stale_session" && (
            <>
              <AlertTriangle className="h-14 w-14 text-amber-500" />
              <h1 className="text-2xl font-bold">Session Expired</h1>
              <p className="text-sm text-muted-foreground">
                We couldn't recover your booking details. If you were charged, please contact
                support.
              </p>
              <Button asChild variant="outline" className="mt-2 w-full">
                <Link to="/">Home</Link>
              </Button>
            </>
          )}

          {pageState.status === "booking_error" && (
            <>
              <AlertTriangle className="h-14 w-14 text-amber-500" />
              <h1 className="text-2xl font-bold">Booking Error</h1>
              <p className="text-sm text-muted-foreground">{pageState.message}</p>
              <Button asChild variant="outline" className="mt-2 w-full">
                <Link to={pageState.returnUrl}>Go back</Link>
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
